import { useEffect, useMemo, useRef, useState } from 'react';
import { AxiosError } from 'axios';
import { QRCodeSVG } from 'qrcode.react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useAuth } from '@/auth/AuthProvider';
import { useI18n } from '@/i18n';
import VariboxStatusBadge from '@/components/VariboxStatusBadge';
import type { VariboxRecord } from '@/data/variboxMockData';
import { getLocationTree, type ApiLocationNode } from '@/services/locationApi';
import { getVariboxById, mapApiVariboxToView, type VariboxViewRecord, updateVaribox } from '@/services/variboxApi';

interface VariboxDetailsDialogProps {
  open: boolean;
  record: (VariboxRecord & { id?: string }) | VariboxViewRecord | null;
  onClose: () => void;
  onEdit?: () => void;
  defaultHistoryVisible?: boolean;
  onUpdated?: () => void | Promise<void>;
}

type LocationOption = {
  id: string;
  parentId: string | null;
  label: string;
  depth: number;
  kind: string;
  rootCode: string | null;
};

function formatLocationLabel(item: ApiLocationNode) {
  if (item.code && item.code.trim().toLowerCase() === item.name.trim().toLowerCase()) {
    return item.name.trim();
  }

  const codePart = item.code ? `${item.code} ` : '';
  return `${codePart}${item.name}`.trim();
}

function flattenLocations(nodes: Awaited<ReturnType<typeof getLocationTree>>['items']): LocationOption[] {
  const flattened: LocationOption[] = [];

  const walk = (items: typeof nodes, depth: number, parentId: string | null, rootCode: string | null) => {
    for (const item of items) {
      const prefix = depth > 0 ? `${'  '.repeat(depth)}- ` : '';
      const currentRootCode = depth === 0 ? item.code : rootCode;
      flattened.push({
        id: item.id,
        parentId,
        depth,
        kind: item.kind,
        rootCode: currentRootCode,
        label: `${prefix}${formatLocationLabel(item)}`.trim(),
      });
      walk(item.children, depth + 1, item.id, currentRootCode);
    }
  };

  walk(nodes, 0, null, null);
  return flattened;
}

export default function VariboxDetailsDialog({
  open,
  record,
  onClose,
  onEdit,
  defaultHistoryVisible = true,
  onUpdated,
}: VariboxDetailsDialogProps) {
  const { t } = useI18n();
  const { user } = useAuth();
  const [showHistory, setShowHistory] = useState(defaultHistoryVisible);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [loadedRecord, setLoadedRecord] = useState<VariboxViewRecord | null>(null);
  const [locationOptions, setLocationOptions] = useState<LocationOption[]>([]);
  const [selectedRootLocationId, setSelectedRootLocationId] = useState('');
  const [selectedLocationId, setSelectedLocationId] = useState('');
  const [adminError, setAdminError] = useState('');
  const [adminSuccess, setAdminSuccess] = useState('');
  const [adminSaving, setAdminSaving] = useState(false);
  const [qrCodeVisible, setQrCodeVisible] = useState(false);
  const qrCodeRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    if (open) {
      setShowHistory(defaultHistoryVisible);
    }
  }, [defaultHistoryVisible, open, record]);

  useEffect(() => {
    let cancelled = false;

    const loadDetails = async () => {
      if (!open || !record || !('id' in record) || !record.id) {
        setLoadedRecord(null);
        setErrorMessage('');
        return;
      }

      setLoading(true);
      setErrorMessage('');

      try {
        const data = await getVariboxById(record.id);
        if (!cancelled) {
          setLoadedRecord(mapApiVariboxToView(data));
        }
      } catch {
        if (!cancelled) {
          setErrorMessage(t('assets.detailsApiError'));
          setLoadedRecord(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadDetails();

    return () => {
      cancelled = true;
    };
  }, [open, record, t]);

  const displayRecord = useMemo(() => {
    if (loadedRecord) {
      return loadedRecord;
    }

    return record;
  }, [loadedRecord, record]);

  const displayRecordId = useMemo(() => {
    if (!displayRecord || !("id" in displayRecord)) {
      return null;
    }

    const candidate = displayRecord.id;
    return typeof candidate === 'string' && candidate ? candidate : null;
  }, [displayRecord]);

  const displayCurrentLocationId = useMemo(() => {
    if (!displayRecord || !('currentLocationId' in displayRecord)) {
      return null;
    }

    return typeof displayRecord.currentLocationId === 'string' ? displayRecord.currentLocationId : null;
  }, [displayRecord]);

  const isAdmin = user?.role === 'Admin';
  const canManageAdminActions = isAdmin && Boolean(displayRecordId);

  const history = useMemo(() => displayRecord?.history ?? [], [displayRecord]);

  const qrPayload = useMemo(() => {
    if (!displayRecord) {
      return '';
    }

    const barcode = 'barcode' in displayRecord && typeof displayRecord.barcode === 'string'
      ? displayRecord.barcode
      : displayRecord.radexId;

    return JSON.stringify({
      type: 'assetflow.varibox',
      version: 1,
      id: displayRecordId,
      barcode,
      serial_number: displayRecord.serialNumber,
      radex_identifier: displayRecord.radexId,
      capacity_l: displayRecord.capacityL,
      color: displayRecord.bodyColor,
      seal_type: displayRecord.sealType,
      status: displayRecord.status,
      contract: displayRecord.contract,
      location: displayRecord.lastLocation,
    });
  }, [displayRecord, displayRecordId]);

  const printQrLabel = () => {
    const svg = qrCodeRef.current?.outerHTML;
    if (!svg || !displayRecord) {
      return;
    }

    const printWindow = window.open('', '_blank', 'width=520,height=680');
    if (!printWindow) {
      window.alert(t('assets.variboxDetails.qrPopupBlocked'));
      return;
    }

    const escapeHtml = (value: string) => value.replace(/[&<>"']/g, (character) => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;',
    })[character] ?? character);
    const labelRows = [
      [t('assets.createDialog.serialNumber'), displayRecord.serialNumber],
      [t('assets.createDialog.barcode'), 'barcode' in displayRecord && typeof displayRecord.barcode === 'string' ? displayRecord.barcode : displayRecord.radexId],
      ['RADEX', displayRecord.radexId],
      [t('assets.variboxDetails.capacity'), `${displayRecord.capacityL} L`],
      [t('assets.variboxDetails.color'), displayRecord.bodyColor],
      [t('assets.variboxDetails.seal'), displayRecord.sealType],
      [t('assets.variboxDetails.lastLocation'), displayRecord.lastLocation],
    ];

    printWindow.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>${escapeHtml(displayRecord.serialNumber)}</title><style>body{font-family:Arial,sans-serif;color:#172033;margin:24px}.label{max-width:360px;margin:auto;border:1px solid #cbd5e1;border-radius:12px;padding:20px;text-align:center}.label svg{width:220px;height:220px}.serial{font-size:24px;font-weight:700;margin:12px 0}.data{text-align:left;font-size:13px;line-height:1.6}.data div{border-top:1px solid #e2e8f0;padding:4px 0}.data strong{display:inline-block;min-width:120px}@media print{body{margin:0}.label{border:0}}</style></head><body><main class="label">${svg}<div class="serial">${escapeHtml(displayRecord.serialNumber)}</div><section class="data">${labelRows.map(([label, value]) => `<div><strong>${escapeHtml(label)}</strong>${escapeHtml(value)}</div>`).join('')}</section></main></body></html>`);
    printWindow.document.close();
    printWindow.focus();
    window.setTimeout(() => printWindow.print(), 250);
  };

  const locationById = useMemo(() => {
    const map = new Map<string, LocationOption>();
    for (const option of locationOptions) {
      map.set(option.id, option);
    }
    return map;
  }, [locationOptions]);

  const rootOptions = useMemo(() => {
    return locationOptions.filter((option) => (option.depth === 0 || option.kind === 'root') && (option.rootCode === 'PL01' || option.rootCode === 'PL99'));
  }, [locationOptions]);

  const sublocationOptions = useMemo(() => {
    if (!selectedRootLocationId) {
      return [];
    }

    return locationOptions.filter((option) => option.depth > 0 && option.label && isUnderRoot(option, selectedRootLocationId, locationById));
  }, [locationById, locationOptions, selectedRootLocationId]);

  function isUnderRoot(option: LocationOption, rootId: string, optionMap: Map<string, LocationOption>) {
    let currentParent = option.parentId;
    while (currentParent) {
      if (currentParent === rootId) {
        return true;
      }
      currentParent = optionMap.get(currentParent)?.parentId ?? null;
    }
    return false;
  }

  useEffect(() => {
    setAdminError('');
    setAdminSuccess('');
    if (!displayCurrentLocationId) {
      setSelectedRootLocationId('');
      setSelectedLocationId('');
      return;
    }

    const current = locationById.get(displayCurrentLocationId);
    if (!current) {
      setSelectedRootLocationId('');
      setSelectedLocationId(displayCurrentLocationId ?? '');
      return;
    }

    if (current.depth === 0 || current.kind === 'root') {
      setSelectedRootLocationId(current.id);
      setSelectedLocationId(current.id);
      return;
    }

    let parentId = current.parentId;
    let rootId = '';
    while (parentId) {
      const parent = locationById.get(parentId);
      if (!parent) {
        break;
      }
      if (parent.depth === 0 || parent.kind === 'root') {
        rootId = parent.id;
        break;
      }
      parentId = parent.parentId;
    }

    setSelectedRootLocationId(rootId);
    setSelectedLocationId(displayCurrentLocationId ?? '');
  }, [displayCurrentLocationId, locationById]);

  useEffect(() => {
    let cancelled = false;

    const loadLocations = async () => {
      if (!open || !canManageAdminActions) {
        setLocationOptions([]);
        return;
      }

      try {
        const data = await getLocationTree();
        if (!cancelled) {
          setLocationOptions(flattenLocations(data.items));
        }
      } catch {
        if (!cancelled) {
          setLocationOptions([]);
        }
      }
    };

    loadLocations();

    return () => {
      cancelled = true;
    };
  }, [canManageAdminActions, open]);

  const applyAdminChanges = async (activate: boolean) => {
    if (!displayRecordId || !displayRecord || !canManageAdminActions) {
      return;
    }

    const payload: { status?: string; current_location_id?: string | null } = {};

    if (activate && displayRecord.status !== 'available') {
      payload.status = 'wolny';
    }

    if (selectedLocationId && selectedLocationId !== displayCurrentLocationId) {
      payload.current_location_id = selectedLocationId;
    }

    if (!payload.status && !payload.current_location_id) {
      setAdminSuccess(t('assets.variboxDetails.adminNoChanges'));
      setAdminError('');
      return;
    }

    setAdminSaving(true);
    setAdminError('');
    setAdminSuccess('');

    try {
      const updated = await updateVaribox(displayRecordId, payload);
      setLoadedRecord(mapApiVariboxToView(updated));
      setAdminSuccess(t('assets.variboxDetails.adminSaved'));
      if (onUpdated) {
        await onUpdated();
      }
    } catch (error) {
      let detail = t('assets.variboxDetails.adminSaveError');
      if (error instanceof AxiosError) {
        const apiDetail = error.response?.data?.detail;
        if (typeof apiDetail === 'string' && apiDetail.trim()) {
          detail = apiDetail;
        }
      }
      setAdminError(detail);
    } finally {
      setAdminSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{t('assets.variboxDetails.title')}</DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Typography variant="body2" color="text.secondary">{t('assets.loadingDetails')}</Typography>
        ) : null}

        {!loading && errorMessage ? (
          <Alert severity="error">{errorMessage}</Alert>
        ) : null}

        {!loading && displayRecord ? (
          <Stack spacing={2.5}>
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={2}>
              <Box>
                <Typography variant="h6">{displayRecord.serialNumber}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {displayRecord.radexId} · {displayRecord.capacityL} L · {displayRecord.bodyColor}
                </Typography>
              </Box>
              <VariboxStatusBadge status={displayRecord.status} />
            </Stack>

            <Card variant="outlined">
              <CardContent>
                <Stack spacing={1.5}>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{t('assets.variboxDetails.qrTitle')}</Typography>
                    <Typography variant="body2" color="text.secondary">{t('assets.variboxDetails.qrDescription')}</Typography>
                  </Box>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
                    {qrCodeVisible ? (
                      <Box sx={{ p: 1, bgcolor: '#fff', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                        <QRCodeSVG ref={qrCodeRef} value={qrPayload} size={200} level="Q" includeMargin title={t('assets.variboxDetails.qrTitle')} />
                      </Box>
                    ) : null}
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                      <Button variant={qrCodeVisible ? 'outlined' : 'contained'} onClick={() => setQrCodeVisible((visible) => !visible)}>
                        {qrCodeVisible ? t('assets.variboxDetails.qrHide') : t('assets.variboxDetails.qrGenerate')}
                      </Button>
                      {qrCodeVisible ? <Button variant="outlined" onClick={printQrLabel}>{t('assets.variboxDetails.qrPrint')}</Button> : null}
                    </Stack>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="caption" color="text.secondary">{t('assets.variboxDetails.capacity')}</Typography>
                <Typography variant="body1">{displayRecord.capacityL} L</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="caption" color="text.secondary">{t('assets.variboxDetails.color')}</Typography>
                <Typography variant="body1">{displayRecord.bodyColor}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="caption" color="text.secondary">{t('assets.variboxDetails.seal')}</Typography>
                <Typography variant="body1">{displayRecord.sealType}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="caption" color="text.secondary">{t('assets.variboxDetails.client')}</Typography>
                <Typography variant="body1">{displayRecord.client}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="caption" color="text.secondary">{t('assets.variboxDetails.contract')}</Typography>
                <Typography variant="body1">{displayRecord.contract}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="caption" color="text.secondary">RFID</Typography>
                <Typography variant="body1">{displayRecord.rfid}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={6}>
                <Typography variant="caption" color="text.secondary">{t('assets.variboxDetails.lastLocation')}</Typography>
                <Typography variant="body1">{displayRecord.lastLocation}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={6}>
                <Typography variant="caption" color="text.secondary">{t('assets.variboxDetails.lastMovement')}</Typography>
                <Typography variant="body1">{displayRecord.lastMovement}</Typography>
              </Grid>
            </Grid>

            {canManageAdminActions ? (
              <Card variant="outlined">
                <CardContent>
                  <Stack spacing={1.5}>
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        {t('assets.variboxDetails.adminTitle')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t('assets.variboxDetails.adminDescription')}
                      </Typography>
                    </Box>

                    {onEdit ? (
                      <Button variant="contained" onClick={onEdit} sx={{ alignSelf: 'flex-start' }}>
                        {t('assets.variboxDetails.editData')}
                      </Button>
                    ) : null}

                    <TextField
                      select
                      fullWidth
                      label={t('assets.variboxDetails.rootLocationLabel')}
                      value={selectedRootLocationId}
                      onChange={(event) => {
                        const nextRootId = event.target.value;
                        setSelectedRootLocationId(nextRootId);
                        setSelectedLocationId('');
                      }}
                      disabled={adminSaving}
                    >
                      <MenuItem value="">{t('assets.variboxDetails.rootLocationPlaceholder')}</MenuItem>
                      {rootOptions.map((location) => (
                        <MenuItem key={location.id} value={location.id}>{location.label}</MenuItem>
                      ))}
                    </TextField>

                    <TextField
                      select
                      fullWidth
                      label={t('assets.variboxDetails.sublocationLabel')}
                      value={selectedLocationId}
                      onChange={(event) => setSelectedLocationId(event.target.value)}
                      disabled={adminSaving || !selectedRootLocationId}
                      helperText={
                        !selectedRootLocationId
                          ? t('assets.variboxDetails.sublocationSelectRootHint')
                          : sublocationOptions.length === 0
                            ? t('assets.variboxDetails.sublocationNoOptionsHint')
                            : undefined
                      }
                    >
                      {sublocationOptions.map((location) => (
                        <MenuItem key={location.id} value={location.id}>{location.label}</MenuItem>
                      ))}
                    </TextField>

                    {adminError ? <Alert severity="error">{adminError}</Alert> : null}
                    {adminSuccess ? <Alert severity="success">{adminSuccess}</Alert> : null}

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                      <Button
                        variant="contained"
                        disabled={adminSaving || !selectedLocationId}
                        onClick={() => applyAdminChanges(true)}
                      >
                        {t('assets.variboxDetails.activateAndSave')}
                      </Button>
                      <Button
                        variant="outlined"
                        disabled={adminSaving || !selectedLocationId}
                        onClick={() => applyAdminChanges(false)}
                      >
                        {t('assets.variboxDetails.saveLocation')}
                      </Button>
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            ) : null}

            <Card variant="outlined">
              <CardContent>
                <Stack spacing={1.5}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{t('assets.variboxDetails.historyTitle')}</Typography>
                      <Typography variant="body2" color="text.secondary">{t('assets.variboxDetails.historyDescription')}</Typography>
                    </Box>
                    <Button variant="outlined" onClick={() => setShowHistory((current) => !current)}>
                      {t('assets.buttons.viewHistory')}
                    </Button>
                  </Stack>

                  {showHistory ? (
                    <Stack spacing={1.5}>
                      {history.map((entry) => (
                        <Box key={`${displayRecord.serialNumber}-${entry.date}`} sx={{ p: 1.5, borderRadius: 2, backgroundColor: 'rgba(20,63,107,0.04)' }}>
                          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{entry.date}</Typography>
                            <Typography variant="caption" color="text.secondary">{entry.location}</Typography>
                          </Stack>
                          <Typography variant="body2" color="text.secondary">{entry.description}</Typography>
                        </Box>
                      ))}

                      {history.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">{t('assets.historyUnavailable')}</Typography>
                      ) : null}
                    </Stack>
                  ) : null}
                </Stack>
              </CardContent>
            </Card>
          </Stack>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.buttons.close')}</Button>
        <Button variant="contained" onClick={onClose}>{t('common.buttons.keepExploring')}</Button>
      </DialogActions>
    </Dialog>
  );
}
